import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import { useMediaQuery } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { userState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import { getLockedPage, StockRef } from "@/lib/lockedPages";
import { AdvancedOffer, DEFAULT_OFFER, loadAdvancedOffer } from "@/lib/advancedOffer";
import { trackLockedPanel } from "@/lib/tracking";
import { getLandingUtm } from "@/lib/landingAttribution";
import { usePageTour } from "@/components/tour/usePageTour";
import styles from "./LockedValuePanel.module.scss";

/**
 * Signed-out selling layer for locked data pages.
 *
 * A docked card (bottom-right on desktop, a bottom bar that expands into a
 * sheet on phones) rather than a modal: the visitor can keep reading the page
 * behind it. It says what the page is, what ORTEX Advanced adds on this page,
 * the price with the first-month offer, and two ways in.
 *
 * Shown only to signed-out visitors ("ORTEX Visitor") on the web, and only on
 * the pages listed in `src/lib/lockedPages.ts`. Never inside the native apps:
 * store rules forbid linking out to a web checkout there. Logged-in users,
 * free or paid, never see it.
 *
 * Collapsing is remembered for the browser session, so it does not reopen on
 * every page the visitor moves to.
 */

const COLLAPSED_KEY = "ortex.lockedPanel.collapsed";

const readCollapsed = (fallback: boolean): boolean => {
    try {
        const v = window.sessionStorage.getItem(COLLAPSED_KEY);
        return v === null ? fallback : v === "1";
    } catch {
        return fallback;
    }
};

const writeCollapsed = (value: boolean) => {
    try {
        window.sessionStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
    } catch {
        /* private mode */
    }
};

const LockedValuePanel = ({ stock }: { stock?: StockRef }) => {
    const { pathname, asPath } = useRouter();
    const user = useRecoilValue(userState);
    const isPhone = useMediaQuery("(max-width: 768px)");
    const tour = usePageTour();

    const page = getLockedPage(pathname);
    const visible = !!page && user?.subscription_plan === "ORTEX Visitor" && !isOrtexApp();

    const [offer, setOffer] = useState<AdvancedOffer>(DEFAULT_OFFER);
    // Phones start as the slim bar (the page stays readable); desktop starts open.
    const [collapsed, setCollapsed] = useState<boolean>(true);

    useEffect(() => {
        if (!visible) return;
        setCollapsed(readCollapsed(isPhone));
        let cancelled = false;
        loadAdvancedOffer().then((o) => {
            if (!cancelled) setOffer(o);
        });
        return () => {
            cancelled = true;
        };
    }, [visible, isPhone]);

    const trackProps = () => ({
        page: page?.id,
        ticker: stock?.ticker,
        exchange: stock?.exchangesymbol,
        currency: offer.currency,
        offer: offer.offerPrice,
        device: isPhone ? "phone" : "desktop",
        ...getLandingUtm(),
    });

    // One view per page shown (asPath, so moving between tickers counts each).
    const lastViewed = useRef<string>("");
    useEffect(() => {
        if (!visible || lastViewed.current === asPath) return;
        lastViewed.current = asPath;
        trackLockedPanel("view", { ...trackProps(), collapsed: readCollapsed(isPhone) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, asPath]);

    if (!visible || !page) return null;

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(next);
        trackLockedPanel(next ? "collapse" : "expand", trackProps());
    };

    const offerCta = `Get your first month for ${offer.offerPrice}`;
    const priceLine = (
        <p className={styles.price}>
            <strong>{offer.offerPrice}</strong> for your first month, then {offer.fullPrice} a month. Cancel anytime.
        </p>
    );

    const ctas = (
        <div className={styles.ctas}>
            {/* A full page load, like the ad links: _app picks the promo up from the URL and
                carries it through sign-up into the checkout (src/lib/checkoutIntent.ts). */}
            <a className={styles.primary} href={offer.offerHref} rel="nofollow" onClick={() => trackLockedPanel("first_month_offer", trackProps())}>
                {offerCta}
            </a>
            <Link href="/login?register=true">
                <a className={styles.secondary} rel="nofollow" onClick={() => trackLockedPanel("start_free", trackProps())}>
                    Start free
                </a>
            </Link>
        </div>
    );

    if (collapsed) {
        return (
            <>
                {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
                <aside className={`${styles.panel} ${styles.collapsed}`} aria-label="Unlock this page with ORTEX Advanced">
                    <button type="button" className={styles.collapsedText} onClick={toggle} aria-expanded={false}>
                        <span className={styles.eyebrow}>ORTEX Advanced</span>
                        <span className={styles.collapsedLine}>
                            Unlock live data · <strong>{offer.offerPrice}</strong> first month
                        </span>
                    </button>
                    <a className={`${styles.primary} ${styles.compact}`} href={offer.offerHref} rel="nofollow" onClick={() => trackLockedPanel("first_month_offer", { ...trackProps(), collapsed: true })}>
                        {isPhone ? "Start" : offerCta}
                    </a>
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Show what Advanced unlocks">
                        <ExpandLessIcon fontSize="small" />
                    </button>
                </aside>
            </>
        );
    }

    return (
        <>
            {isPhone && <div className={styles.phoneSpacer} aria-hidden />}
            {isPhone && <div className={styles.scrim} onClick={toggle} aria-hidden />}
            <aside className={styles.panel} aria-label="Unlock this page with ORTEX Advanced">
                <div className={styles.header}>
                    <span className={styles.eyebrow}>Unlock this page with ORTEX Advanced</span>
                    <button type="button" className={styles.iconButton} onClick={toggle} aria-label="Minimise" aria-expanded>
                        <ExpandMoreIcon fontSize="small" />
                    </button>
                </div>
                <h2 className={styles.headline}>{page.headline(stock)}</h2>
                <p className={styles.sub}>The free view is limited. ORTEX Advanced adds:</p>
                <ul className={styles.unlocks}>
                    {page.unlocks.map((u) => (
                        <li key={u}>
                            <CheckIcon className={styles.check} fontSize="inherit" />
                            <span>{u}</span>
                        </li>
                    ))}
                </ul>
                {page.note && <p className={styles.note}>{page.note}</p>}
                {priceLine}
                {ctas}
                {tour && (
                    <button
                        type="button"
                        className={styles.tourLink}
                        onClick={() => {
                            trackLockedPanel("tour", trackProps());
                            if (isPhone) {
                                setCollapsed(true);
                                writeCollapsed(true);
                            }
                            tour.start();
                        }}
                    >
                        New here? Take the 30-second tour
                    </button>
                )}
            </aside>
        </>
    );
};

export default LockedValuePanel;
